import {Component, computed, HostListener, inject, OnDestroy, OnInit} from '@angular/core';
import {RouterLink} from '@angular/router';
import {Popover} from 'primeng/popover';
import {Tooltip} from 'primeng/tooltip';
import {SeiService} from '@core/services/sei.service';

/**
 * SEI na barra do topo (igual ao SIGEP): logo do SEI com bolinha verde quando o
 * usuário está conectado ao SEI, cinza quando não, laranja sem a extensão (ou
 * com versão nova). O menu tem "Entrar no SEI" (pela Minha Área), o Bot SisNot ↔
 * SEI e como instalar/atualizar a extensão. Quem descobre a conexão é a
 * extensão "SisNot ↔ SEI"; conectado, o número SEI das telas vira link
 * (SeiService.ativo). Só entra no topo para quem tem a integração liberada.
 */
@Component({
  selector: 'app-sei-status',
  imports: [Popover, Tooltip, RouterLink],
  template: `
    <button type="button" class="sei-btn" [attr.data-estado]="sei.estado()" [class.sei-aviso]="atualizar()"
            [pTooltip]="titulo()" tooltipPosition="bottom" aria-label="Conexão com o SEI" (click)="menu.toggle($event)">
      <span class="sei-logo" aria-hidden="true">sei</span>
      <span class="sei-dot" aria-hidden="true"></span>
    </button>

    <p-popover #menu>
      <div class="w-[300px] text-sm">
        @switch (sei.estado()) {
          @case ('logado') {
            <div class="font-semibold mb-1 text-green-700 dark:text-green-400"><i class="pi pi-check-circle mr-1"></i>Conectado ao SEI</div>
            <div class="text-xs text-gray-500 dark:text-gray-400 mb-2">Sua sessão do SEI está aberta neste navegador: o número SEI das telas abre o processo no SEI.</div>
            <div class="flex flex-col gap-2">
              <a class="sei-acao" routerLink="/bot-sei" (click)="menu.hide()"><i class="pi pi-search mr-1"></i>Bot SisNot ↔ SEI</a>
              <a class="sei-acao sei-acao-contorno" href="https://sei.sp.gov.br/sei/" target="_blank" rel="noopener">Abrir o SEI</a>
            </div>
          }
          @case ('deslogado') {
            <div class="font-semibold mb-1"><i class="pi pi-exclamation-circle mr-1"></i>Você não está conectado ao SEI</div>
            <div class="text-xs text-gray-500 dark:text-gray-400 mb-2">Entre no SEI neste navegador para o SisNot poder abrir os processos lá. Depois de entrar, volte aqui: a bolinha fica verde sozinha.</div>
            <a class="sei-acao" [href]="minhaArea" target="_blank" rel="noopener">Entrar no SEI</a>
          }
          @case ('sem-extensao') {
            <div class="font-semibold mb-1"><i class="pi pi-download mr-1"></i>Falta a extensão SisNot ↔ SEI</div>
            <div class="text-xs text-gray-500 dark:text-gray-400 mb-2">Com ela, o número SEI das telas abre o processo no SEI e o SEI ganha o botão "Abrir no SisNot". Para instalar no Chrome:</div>
            <ol class="text-xs list-decimal pl-4 space-y-1">
              <li><a class="underline font-semibold" [href]="zip" download>Baixe o arquivo</a> e use <strong>Extrair tudo</strong> (fica a pasta <strong>sisnot-sei</strong> com o arquivo <code>manifest.json</code> dentro).</li>
              <li>Abra <code>chrome://extensions</code> e ligue o <strong>Modo do desenvolvedor</strong> (canto superior direito).</li>
              <li>Clique em <strong>Carregar sem compactação</strong> e escolha a pasta <strong>sisnot-sei</strong>.</li>
              <li>Recarregue esta página.</li>
            </ol>
          }
          @case ('antiga') {
            <div class="font-semibold mb-1"><i class="pi pi-arrow-circle-up mr-1"></i>Atualize a extensão</div>
            <div class="text-xs text-gray-500 dark:text-gray-400">Essa versão da extensão não sabe dizer se você está no SEI. Baixe a nova no link abaixo.</div>
          }
          @case ('erro') {
            <div class="font-semibold mb-1">Não foi possível falar com o SEI</div>
            <div class="text-xs text-gray-500 dark:text-gray-400">O SEI pode estar fora do ar. Tentamos de novo em instantes.</div>
          }
          @default {
            <div class="font-semibold">Verificando o SEI…</div>
          }
        }

        @if (sei.extensao(); as versao) {
          <div class="text-xs border-t border-gray-200 dark:border-zinc-700 mt-3 pt-2">
            @if (atualizar()) {
              <div class="font-semibold text-amber-700 dark:text-amber-400 mb-1"><i class="pi pi-arrow-circle-up mr-1"></i>Há uma versão nova da extensão</div>
              <div class="text-gray-500 dark:text-gray-400">Extensão {{ versao }}</div>
              <div>
                <a class="underline font-semibold" [href]="zip" download>Baixar a versão {{ versaoAtual }}</a>
                <span class="text-gray-500 dark:text-gray-400"> — extraia por cima da pasta e clique em recarregar (↻) em chrome://extensions</span>
              </div>
            } @else {
              <div class="text-gray-500 dark:text-gray-400">Extensão {{ versao }} · atualizada</div>
            }
          </div>
        }
      </div>
    </p-popover>
  `,
  styles: `
    :host { display: inline-flex; align-items: center; }
    .sei-btn { position: relative; display: inline-flex; align-items: center; justify-content: center; width: 40px; height: 40px;
      border-radius: 9999px; border: 0; background: transparent; padding: 0; cursor: pointer; transition: background .15s; }
    .sei-btn:hover { background: rgba(255,255,255,.12); }
    .sei-btn.sei-aviso { box-shadow: inset 0 0 0 2px #f59e0b; }
    .sei-logo { font: 800 13px/1 Arial, sans-serif; letter-spacing: -.5px; color: #fff; background: #0978be; border-radius: 5px; padding: 3px 4px 4px; }
    .sei-dot { position: absolute; right: 5px; bottom: 6px; width: 10px; height: 10px; border-radius: 50%; background: #9ca3af; border: 2px solid #1a1a1a; }
    [data-estado="logado"] .sei-dot { background: #22c55e; }
    [data-estado="sem-extensao"] .sei-dot, [data-estado="antiga"] .sei-dot { background: #f59e0b; }
    [data-estado="erro"] .sei-dot { background: #ef4444; }
    [data-estado="verificando"] .sei-dot { animation: seiPisca 1s infinite; }
    @keyframes seiPisca { 50% { opacity: .35; } }
    .sei-acao { display: block; width: 100%; text-align: center; padding: 6px 10px; border-radius: 6px; font-weight: 600;
      background: #E41B2E; color: #fff; border: 1px solid #E41B2E; }
    .sei-acao:hover { filter: brightness(.92); }
    .sei-acao-contorno { background: transparent; color: #E41B2E; }
  `,
})
export class SeiStatus implements OnInit, OnDestroy {
  protected sei = inject(SeiService);
  protected readonly minhaArea = SeiService.MINHA_AREA;
  protected readonly zip = 'assets/extensao/sisnot-sei.zip';
  protected readonly versaoAtual = SeiService.VERSAO_ATUAL;

  protected atualizar = computed(() => this.sei.desatualizada() || this.sei.estado() === 'antiga');
  protected titulo = computed(() => ({
    verificando: 'SEI: verificando conexão…',
    logado: 'SEI: conectado',
    deslogado: 'SEI: não conectado — clique para entrar',
    'sem-extensao': 'SEI: falta instalar a extensão SisNot ↔ SEI',
    antiga: 'SEI: atualize a extensão SisNot ↔ SEI',
    erro: 'SEI: não foi possível verificar',
  })[this.sei.estado()]);

  private intervalo?: ReturnType<typeof setInterval>;
  private inicio?: ReturnType<typeof setTimeout>;

  ngOnInit() {
    // a extensão marca a página logo no início; espera um pouco antes de dizer que não tem
    this.inicio = setTimeout(() => this.sei.verificar(), 400);
    // confere de novo a cada 2 min (e ao voltar pra aba, ver onFocus)
    this.intervalo = setInterval(() => { if (!document.hidden) this.sei.verificar(); }, 120000);
  }

  ngOnDestroy() {
    clearTimeout(this.inicio);
    clearInterval(this.intervalo);
  }

  // ex.: depois de entrar no SEI, ao voltar pra aba do SisNot a bolinha fica verde
  @HostListener('window:focus')
  onFocus() {
    this.sei.verificar();
  }
}
